/**
 * useStaffAdmin — ростер кадров для HR (OO · Кабинет → HR и EO → HR). 03.10 (владелец:
 * «ОО нанимает и увольняет операторов, остальных — ИО»; «увольнение снимает будущую
 * работу»; «доступ новому — ссылкой в бота») — переписан под найм/увольнение/возврат.
 * Запросы и разбор — hooks/staffApi.ts.
 *
 * Сохранения одной строки идут СТРОГО по очереди (очередь промисов на id): прежний
 * EoHrView считал роли от строки на момент рендера, и два быстрых тапа по чипам
 * перетирали друг друга. Следующий набор ролей считается от staffRef (последнее
 * оптимистичное состояние), откат при ошибке — тихая перезагрузка с сервера.
 * Тихая перезагрузка (refreshQuiet) не трогает строки с несохранённой правкой.
 * onChanged — после найма/увольнения/возврата/правки ролей и статуса (ОО: доска
 * board.reload, чтобы Грейды/Ставки/Премии сразу видели состав).
 * 03.10 (владелец: «продолжи»): setProfile — имя, ник, дата найма после найма; так же
 * оптимистично и с откатом через refreshQuiet, как setStatus.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { StaffRoleId } from "@ub/shared/staffRoles";
import {
  errText, fetchRoster, putStaff, putBoardHidden, putHistoryAll, hireStaff, fetchFirePreview, fireStaff, restoreStaff, issueInvite,
  type StaffRow, type StaffStatus, type HireInput, type HireResult, type FirePreview, type FireResult, type Invite,
} from "./staffApi";

export type { StaffRow, StaffStatus, Invite } from "./staffApi";

/** Правка карточки после найма: только изменённые поля (nickname "" — стереть). */
export interface ProfilePatch { name?: string; nickname?: string; hiredAt?: string }

type Res<T = undefined> = { ok: true; value: T } | { ok: false; error: string };

export interface UseStaffAdminResult {
  staff: StaffRow[];
  loading: boolean;
  error: string | null;
  /** id строк, у которых идёт сохранение. */
  busy: ReadonlySet<string>;
  /** Ошибка последнего сохранения строки (пусто — нет). */
  rowErr: Readonly<Record<string, string>>;
  reload: () => void;
  refreshQuiet: () => Promise<void>;
  setStatus: (id: string, status: "active" | "vacation") => void;
  toggleRole: (id: string, role: StaffRoleId) => void;
  setPhone: (id: string, phone: string) => void;
  setProfile: (id: string, patch: ProfilePatch) => void;
  /** 16.10: «не показывать в расстановке» (оптимистично, откат — refreshQuiet, как статус) */
  setBoardHidden: (id: string, hidden: boolean) => void;
  /** 27.10: «видит всю историю клиентов» (так же оптимистично) */
  setHistoryAll: (id: string, on: boolean) => void;
  hire: (input: HireInput) => Promise<HireResult>;
  firePreview: (id: string) => Promise<Res<FirePreview>>;
  fire: (id: string, reason: string) => Promise<Res<FireResult>>;
  restore: (id: string) => Promise<Res<Invite | null>>;
  invite: (id: string, unbind?: boolean) => Promise<Res<Invite>>;
}

export function useStaffAdmin(opts: { onChanged?: () => void } = {}): UseStaffAdminResult {
  const [staff, setStaffState] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const [trigger, setTrigger] = useState(0);
  const staffRef = useRef<StaffRow[]>([]);
  const queues = useRef(new Map<string, Promise<unknown>>());
  const pending = useRef(new Map<string, number>());
  const changedRef = useRef(opts.onChanged);
  changedRef.current = opts.onChanged;
  const alive = useRef(true);
  /* true и в теле эффекта: StrictMode и Fast Refresh гоняют эффекты «снять-поставить» */
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const setStaff = useCallback((next: StaffRow[]) => { staffRef.current = next; setStaffState(next); }, []);
  const patchRow = useCallback((id: string, patch: Partial<StaffRow>) => {
    setStaff(staffRef.current.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }, [setStaff]);
  const changed = () => changedRef.current?.();

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    fetchRoster(ac.signal).then(
      (rows) => { if (!ac.signal.aborted) { setStaff(rows); setLoading(false); } },
      (e: unknown) => { if (!ac.signal.aborted) { setStaff([]); setError(errText(e)); setLoading(false); } },
    );
    return () => ac.abort();
  }, [trigger, setStaff]);

  const reload = useCallback(() => setTrigger((n) => n + 1), []);
  const refreshQuiet = useCallback(() =>
    fetchRoster().then((rows) => {
      if (!alive.current) return;
      const keep = new Map(staffRef.current.filter((s) => pending.current.has(s.id)).map((s) => [s.id, s]));
      setStaff(rows.map((r) => keep.get(r.id) ?? r));
    }, () => { /* тихо: ростер на экране остаётся прежним */ }), [setStaff]);

  /** Сохранение строки в её очередь: busy на время, ошибка — в rowErr и перезагрузка. */
  const enqueue = useCallback((id: string, body: Record<string, unknown>, send: typeof putStaff = putStaff) => {
    pending.current.set(id, (pending.current.get(id) ?? 0) + 1);
    setBusy(new Set(pending.current.keys()));
    setRowErr((m) => ({ ...m, [id]: "" }));
    const prev = queues.current.get(id) ?? Promise.resolve();
    const next = prev.catch(() => {}).then(() => send(id, body)).then(
      () => { changedRef.current?.(); },
      (e: unknown) => { if (alive.current) { setRowErr((m) => ({ ...m, [id]: errText(e) })); refreshQuiet(); } },
    ).finally(() => {
      const n = (pending.current.get(id) ?? 1) - 1;
      if (n > 0) pending.current.set(id, n); else pending.current.delete(id);
      if (alive.current) setBusy(new Set(pending.current.keys()));
    });
    queues.current.set(id, next);
  }, [refreshQuiet]);

  const setStatus = useCallback((id: string, status: "active" | "vacation") => {
    patchRow(id, { status });
    enqueue(id, { status });
  }, [patchRow, enqueue]);

  const setBoardHidden = useCallback((id: string, hidden: boolean) => {
    patchRow(id, { boardHidden: hidden });
    enqueue(id, { hidden }, putBoardHidden);
  }, [patchRow, enqueue]);

  const setHistoryAll = useCallback((id: string, on: boolean) => {
    patchRow(id, { historyAll: on });
    enqueue(id, { on }, putHistoryAll);
  }, [patchRow, enqueue]);

  const toggleRole = useCallback((id: string, role: StaffRoleId) => {
    const row = staffRef.current.find((s) => s.id === id);
    if (!row) return;
    const has = row.roles.includes(role);
    if (has && row.roles.length === 1) { setRowErr((m) => ({ ...m, [id]: "Последнюю роль снять нельзя" })); return; }
    const roles = has ? row.roles.filter((r) => r !== role) : [...row.roles, role];
    patchRow(id, { roles });
    enqueue(id, { roles });
  }, [patchRow, enqueue]);

  const setPhone = useCallback((id: string, phone: string) => {
    patchRow(id, { phone });
    enqueue(id, { phone });
  }, [patchRow, enqueue]);

  const setProfile = useCallback((id: string, p: ProfilePatch) => {
    const body: Record<string, unknown> = {};
    const patch: Partial<StaffRow> = {};
    if (p.name !== undefined) { body.name = p.name; patch.name = p.name; }
    if (p.nickname !== undefined) { body.nickname = p.nickname; patch.nickname = p.nickname || null; }
    if (p.hiredAt !== undefined) { body.hired_at = p.hiredAt; patch.hiredAt = p.hiredAt; }
    if (!Object.keys(body).length) return;
    patchRow(id, patch);
    enqueue(id, body);
  }, [patchRow, enqueue]);

  const hire = useCallback(async (input: HireInput) => {
    const r = await hireStaff(input);
    if (r.ok) changed();
    /* 15.10 (Вика Б): и при отказе — ответ мог потеряться, а человек уже нанят; «уже есть» — строка должна быть в списке */
    void refreshQuiet();
    return r;
  }, [refreshQuiet]);

  const firePreview = useCallback(async (id: string): Promise<Res<FirePreview>> => {
    try { return { ok: true, value: await fetchFirePreview(id) }; } catch (e) { return { ok: false, error: errText(e) }; }
  }, []);

  const fire = useCallback(async (id: string, reason: string): Promise<Res<FireResult>> => {
    try {
      const value = await fireStaff(id, reason);
      patchRow(id, { status: "fired", firedAt: new Date(Date.now() + 5 * 3_600_000).toISOString().slice(0, 10) }); /* день по Оренбургу, как orenStamp */
      changed();
      return { ok: true, value };
    } catch (e) { return { ok: false, error: errText(e) }; }
  }, [patchRow]);

  const restore = useCallback(async (id: string): Promise<Res<Invite | null>> => {
    try {
      const value = await restoreStaff(id);
      patchRow(id, { status: "active", firedAt: null, ...(value ? { hasTg: false } : {}) });
      changed();
      return { ok: true, value };
    } catch (e) { return { ok: false, error: errText(e) }; }
  }, [patchRow]);

  const invite = useCallback(async (id: string, unbind = false): Promise<Res<Invite>> => {
    try {
      const value = await issueInvite(id, unbind);
      if (unbind) patchRow(id, { hasTg: false });
      return { ok: true, value };
    } catch (e) { return { ok: false, error: errText(e) }; }
  }, [patchRow]);

  return { staff, loading, error, busy, rowErr, reload, refreshQuiet, setStatus, toggleRole, setPhone, setProfile, setBoardHidden, setHistoryAll, hire, firePreview, fire, restore, invite };
}
